import React, { useState } from 'react';
import { Box, useTheme } from '@mui/material';
import type { DetectionResult } from '../services/api';
import { getSeverityStyles } from './ui/severity';

interface DefectOverlayProps {
  src: string;
  detections: DetectionResult[];
  alt?: string;
  maxHeight?: number;
}

export default function DefectOverlay({
  src,
  detections,
  alt = 'Captured',
  maxHeight = 480,
}: DefectOverlayProps) {
  const theme = useTheme();
  const [imgNatural, setImgNatural] = useState<{ w: number; h: number } | null>(null);

  return (
    <Box
      sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0 }}
      data-testid="defect-overlay"
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        onLoad={(e) => {
          const i = e.currentTarget as HTMLImageElement;
          setImgNatural({ w: i.naturalWidth, h: i.naturalHeight });
        }}
        sx={{ display: 'block', maxWidth: '100%', maxHeight }}
      />
      {imgNatural && detections.length > 0 && (
        <Box
          component="svg"
          viewBox={`0 0 ${imgNatural.w} ${imgNatural.h}`}
          preserveAspectRatio="xMidYMid meet"
          data-testid="defect-overlay-svg"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {detections.map((d, i) => {
            const { color } = getSeverityStyles(theme, d.severity);
            const stroke = Math.max(3, imgNatural.w / 400);
            const labelH = Math.max(24, imgNatural.h * 0.035);
            const labelText = `${d.defect_type.toUpperCase()} ${Math.round(d.confidence * 100)}%`;

            // Use segmentation mask if available (YOLO26 instance segmentation), else fall back to bounding box
            const hasSegmentationMask = d.segmentation_mask && d.segmentation_mask.length > 2;

            // Calculate label position (use bounding box for position reference)
            const labelW = Math.max(d.bounding_box.width, labelText.length * labelH * 0.55);
            const labelY = Math.max(0, d.bounding_box.y - labelH);

            return (
              <g key={i} data-testid="defect-box" data-severity={d.severity}>
                {hasSegmentationMask ? (
                  <>
                    {/* Draw segmentation polygon with semi-transparent fill */}
                    <polygon
                      points={d.segmentation_mask!.map(([x, y]) => `${x},${y}`).join(' ')}
                      fill={color}
                      fillOpacity={0.25}
                      stroke={color}
                      strokeWidth={stroke}
                      data-testid="defect-polygon"
                    />
                  </>
                ) : (
                  <>
                    {/* Fall back to bounding box rectangle */}
                    <rect
                      x={d.bounding_box.x}
                      y={d.bounding_box.y}
                      width={d.bounding_box.width}
                      height={d.bounding_box.height}
                      fill="none"
                      stroke={color}
                      strokeWidth={stroke}
                      data-testid="defect-rect"
                    />
                  </>
                )}
                {/* Label with background */}
                <rect
                  x={d.bounding_box.x}
                  y={labelY}
                  width={labelW}
                  height={labelH}
                  fill={color}
                  opacity={0.92}
                />
                <text
                  x={d.bounding_box.x + labelH * 0.3}
                  y={labelY + labelH * 0.72}
                  fill={theme.palette.common.white}
                  fontSize={labelH * 0.6}
                  fontWeight="bold"
                  fontFamily="sans-serif"
                >
                  {labelText}
                </text>
              </g>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

